import { useState, useCallback, useEffect, useRef } from 'preact/hooks'
import { useAuth } from './hooks/useAuth'
import { useChat } from './hooks/useChat'
import { useChatHistory } from './hooks/useChatHistory'
import { LoginScreen } from './components/LoginScreen'
import { LandingPage } from './components/LandingPage'
import { HomeScreen } from './components/HomeScreen'
import { getHomeLayout, setHomeLayout, STATE_HOME_ENABLED } from './services/homeLayout'
import type { HomeLayout } from './services/homeLayout'
import { Sidebar } from './components/Sidebar'
import { ChatView } from './components/ChatView'
import { VoiceCallScreen } from './components/VoiceCallScreen'
import { FeatureViewShell } from './components/FeatureViewShell'
import { TasksView } from './components/views/TasksView'
import { NotesView } from './components/views/NotesView'
import { ProjectsView } from './components/views/ProjectsView'
import { EmailView } from './components/views/EmailView'
import { CalendarView } from './components/views/CalendarView'
import { CallsView } from './components/views/CallsView'
import { AlertsView } from './components/views/AlertsView'
import { BoxView } from './components/views/BoxView'
import { FilesView } from './components/views/FilesView'
import { SettingsView } from './components/views/SettingsView'
import { UserView } from './components/views/UserView'
import { AppsView } from './components/views/AppsView'
import { deleteCloudSession, renameCloudSession, listCloudPersonas, generateTitle, getPersonaImageKeys, downloadBlob, ensureUserAccount, savePersonaSettings } from './services/api'
import { getSelectedBackend, setSelectedBackend, LOCAL_MODE } from './services/backendConfig'
import { GeneratedContentPreview } from './components/GeneratedContentPreview'
import { precedingUserRequest } from './components/MessageBubble'
import { EmailReviewDialog } from './components/EmailReviewDialog'
import { decryptBytes } from './services/encryption'
import type { CloudPersona } from './services/api'
import type { TtsSettings } from './services/ttsApi'
import { DEFAULT_PERSONAS, PERSONA_INFO } from './types'
import type { Persona, ChatSession } from './types'
import { PRIVATE_MODE, PRIVATE_PERSONA, PRIVATE_TTS_VOICE } from './services/privateConfig'
import { useTheme } from './hooks/useTheme'

type AppView = 'landing' | 'chat'
  | 'tasks' | 'notes' | 'projects' | 'email' | 'calendar'
  | 'calls' | 'alerts' | 'box' | 'files' | 'settings' | 'user' | 'apps'

/** View title mapping for feature views */
const VIEW_TITLES: Partial<Record<AppView, string>> = {
  tasks: 'Tasks',
  notes: 'Notes',
  projects: 'Projects',
  email: 'Email',
  calendar: 'Calendar',
  calls: 'Calls',
  alerts: 'Alerts',
  box: 'Mind Wall',
  files: 'Files',
  settings: 'Settings',
  user: 'User Profile',
  apps: 'Apps',
}

/** Canonical persona display order */
const PERSONA_ORDER = ['pipar', 'alex', 'casey', 'river', 'basil', 'custom']

/** Stock persona profile images shipped with the web client */
const DEFAULT_PERSONA_IMAGES: Record<string, string> = {
  pipar: '/personas/pipar.png',
  alex: '/personas/alex.png',
  casey: '/personas/casey.png',
  river: '/personas/river.png',
  basil: '/personas/basil.png',
  custom: '/personas/custom.png',
}

/** Map a cloud persona record to a display Persona */
function toDisplayPersona(cp: CloudPersona): Persona {
  const info = PERSONA_INFO[cp.id]
  return {
    id: cp.id,
    name: cp.persona_name || info?.name || cp.id,
    emoji: info?.emoji || '\u{1F916}',
  }
}

/** Sort personas by canonical order, unknowns at end */
function sortPersonas(personas: Persona[]): Persona[] {
  return [...personas].sort((a, b) => {
    const ai = PERSONA_ORDER.indexOf(a.id)
    const bi = PERSONA_ORDER.indexOf(b.id)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
}

export function App() {
  const { user, isLoading: authLoading, signIn, signOut } = useAuth()
  const chat = useChat()
  const history = useChatHistory()
  const [personas, setPersonas] = useState<Persona[]>(DEFAULT_PERSONAS)
  const [cloudPersonas, setCloudPersonas] = useState<CloudPersona[]>([])
  const [selectedPersona, setSelectedPersona] = useState<Persona>(DEFAULT_PERSONAS[0])
  const [currentView, setCurrentView] = useState<AppView>('landing')
  const [featureOrigin, setFeatureOrigin] = useState<'landing' | 'chat'>('landing')
  // When the user clicks a search result we set focusId so the destination view
  // can auto-select the matching record. Cleared on subsequent navigations.
  const [focusId, setFocusId] = useState<string | null>(null)
  const [personaImages, setPersonaImages] = useState<Record<string, string>>(DEFAULT_PERSONA_IMAGES)
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const titleGeneratedRef = useRef(false)
  const lastUserIdRef = useRef<string | null>(null)
  const [accountReady, setAccountReady] = useState(false)

  // Reset all per-user state when the signed-in account changes (login,
  // logout, or account switch). Without this, persona images cached as
  // object URLs from the previous user keep showing until manual refresh.
  useEffect(() => {
    const currentUserId = user?.uid ?? null
    if (lastUserIdRef.current === currentUserId) return
    lastUserIdRef.current = currentUserId
    setPersonaImages((prev) => {
      // Revoke any blob URLs we created so memory and the prior account's
      // bytes don't linger after the switch.
      for (const url of Object.values(prev)) {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url) } catch { /* ignore */ }
        }
      }
      return { ...DEFAULT_PERSONA_IMAGES }
    })
    setCloudPersonas([])
    setPersonas(DEFAULT_PERSONAS)
    setSelectedPersona(DEFAULT_PERSONAS[0])
  }, [user])
  const themeCtx = useTheme(accountReady)
  const [voiceLanguage, setVoiceLanguage] = useState<string | undefined>(undefined)
  // A voice call with the selected persona (VoiceCallScreen over the whole window).
  const [callOpen, setCallOpen] = useState(false)

  useEffect(() => {
    if (!accountReady || PRIVATE_MODE) return
    import('./services/api').then(({ setCloudSyncEnabled }) => {
      // Ensure the server-side cloud_sync_enabled flag is true so backend
      // flows that gate on it (inbound/sent email persistence) write to
      // cloud_emails for this user.
      setCloudSyncEnabled(true).catch(() => {})
    }).catch(() => {})
  }, [accountReady])

  // The voice language, from web settings: at sign-in and again after Settings
  // (where it is changed), so calls and spoken replies use it without a reload.
  useEffect(() => {
    if (!accountReady || PRIVATE_MODE || currentView === 'settings') return
    import('./services/api').then(({ loadUserSettings }) => {
      loadUserSettings('web').then((s) => {
        if (s.voice_language) setVoiceLanguage(s.voice_language as string)
      })
    }).catch(() => {})
  }, [accountReady, currentView])

  // Ensure user has a Pipar account after login (creates one with trial if needed)
  // Skip in private mode — no cloud account needed
  useEffect(() => {
    if (!user || PRIVATE_MODE) {
      setAccountReady(!PRIVATE_MODE ? false : true)
      return
    }
    setAccountReady(false)
    ensureUserAccount(user.email || '')
      .then((ok) => {
        if (ok) {
          setAccountReady(true)
        } else if (LOCAL_MODE) {
          // Local mode has no alternate backend to fall back to and signOut is a
          // no-op (synthetic user). The local backend auto-provisions test users,
          // so proceed and let the app load rather than stranding it.
          console.warn('Local account setup returned not-ok, proceeding anyway')
          setAccountReady(true)
        } else if (getSelectedBackend() === 'dev') {
          // Dev backend may have been shut down. Existing users with `dev`
          // cached in localStorage would otherwise be stuck in a sign-out loop
          // because every retry hits the same dead backend. Fall back to
          // production (this reloads the page).
          console.warn('Dev backend unreachable, falling back to production')
          setSelectedBackend('production')
        } else {
          // Account creation failed — sign out so they can try again
          console.warn('Account setup failed, signing out')
          signOut()
        }
      })
      .catch(() => {
        if (LOCAL_MODE) {
          console.warn('Local backend unreachable during account setup, proceeding anyway')
          setAccountReady(true)
        } else if (getSelectedBackend() === 'dev') {
          console.warn('Dev backend unreachable, falling back to production')
          setSelectedBackend('production')
        } else {
          signOut()
        }
      })
  }, [user])

  // Refresh cloud personas and merge with defaults. Personas without a
  // matching cloud_user_memory row fall through to their DEFAULT_PERSONAS
  // entry verbatim — the stock emoji avatar already signals to the user
  // that this persona has no profile picture / customization yet, so no
  // separate "local only" badge is needed.
  const refreshCloudPersonas = useCallback(() => {
    listCloudPersonas()
      .then((cps) => {
        setCloudPersonas(cps)
        // Defensive: ignore any cloud persona whose id isn't one of the
        // canonical six. Earlier versions of the backend's persona memory
        // endpoint accepted name-shaped values like "river moon", which
        // produced phantom rows that rendered as un-customised cards
        // (no avatar, lowercase name) below the real persona row. The
        // backend now rejects those at the writer; this filter keeps any
        // historical phantom rows out of the UI until they're cleaned
        // from cloud_user_memory / cloud_persona_settings.
        const validIds = new Set(DEFAULT_PERSONAS.map((dp) => dp.id))
        const validCps = cps.filter((cp) => {
          if (!validIds.has(cp.id)) {
            console.warn('Ignoring unknown persona id from cloud:', cp.id)
            return false
          }
          return true
        })
        const cloudMap = new Map(validCps.map((cp) => [cp.id, cp]))
        const merged: Persona[] = DEFAULT_PERSONAS.map((dp) => {
          const cp = cloudMap.get(dp.id)
          return cp ? toDisplayPersona(cp) : dp
        })
        const displayPersonas = sortPersonas(merged)
        setPersonas(displayPersonas)
        setSelectedPersona((prev) => {
          const match = displayPersonas.find((p) => p.id === prev.id)
          return match || displayPersonas.find((p) => p.id === 'pipar') || displayPersonas[0]
        })
      })
      .catch((e) => console.warn('Failed to load cloud personas:', e))
  }, [])

  // Load cloud personas when user logs in and has account
  // In private mode, skip cloud persona loading entirely
  useEffect(() => {
    if (!user || !accountReady || PRIVATE_MODE) return
    refreshCloudPersonas()
  }, [user, accountReady])

  // Load persona profile images from cloud (progressively, one at a time)
  // Skip in private mode — no cloud storage available
  useEffect(() => {
    if (!user || !accountReady || PRIVATE_MODE) return
    let cancelled = false
    getPersonaImageKeys().then(async (keys) => {
      for (const [personaId, blobKey] of Object.entries(keys)) {
        if (cancelled) break
        try {
          const bytes = await downloadBlob(blobKey)
          if (!bytes || bytes.length === 0 || cancelled) continue

          // Try decryption first, then use raw bytes as fallback
          let imageBytes: Uint8Array | null = null

          // 1) Check if already a raw image (known magic bytes)
          const isRawImage = (bytes[0] === 0x89 && bytes[1] === 0x50) || // PNG
                             (bytes[0] === 0xFF && bytes[1] === 0xD8) || // JPEG
                             (bytes[0] === 0x47 && bytes[1] === 0x49) || // GIF
                             (bytes[0] === 0x52 && bytes[1] === 0x49)    // RIFF/WebP
          if (isRawImage) {
            imageBytes = bytes
          } else {
            // 2) Try decryption
            const decrypted = await decryptBytes(bytes)
            if (decrypted && decrypted.length > 0) {
              imageBytes = decrypted
            } else {
              // 3) Decryption failed — skip, can't display as image
              console.warn(`Persona image for "${personaId}": decryption failed, skipping`)
              continue
            }
          }

          // Detect MIME type from actual bytes
          let mimeType = 'image/png'
          if (imageBytes[0] === 0xFF && imageBytes[1] === 0xD8) mimeType = 'image/jpeg'
          else if (imageBytes[0] === 0x47 && imageBytes[1] === 0x49) mimeType = 'image/gif'
          else if (imageBytes[0] === 0x52 && imageBytes[1] === 0x49) mimeType = 'image/webp'

          const blob = new Blob([new Uint8Array(imageBytes.buffer as ArrayBuffer, imageBytes.byteOffset, imageBytes.byteLength)], { type: mimeType })
          const url = URL.createObjectURL(blob)
          if (!cancelled) {
            setPersonaImages((prev) => ({ ...prev, [personaId]: url }))
          }
        } catch (e) {
          console.warn(`Failed to load persona image for "${personaId}":`, e)
        }
      }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [user, accountReady])

  // Auto-generate chat title after first message pair completes
  // Skip in private mode — no session history to title
  // Skip for continued sessions (loaded from history) — they already have a title
  const isContinuedSessionRef = useRef(false)
  useEffect(() => {
    if (PRIVATE_MODE) return
    if (titleGeneratedRef.current || !chat.session?.voiceChatId) return
    if (isContinuedSessionRef.current) return
    // Only trigger after streaming is done (no streaming messages)
    if (chat.messages.some((m) => m.isStreaming)) return

    const userMsg = chat.messages.find((m) => m.role === 'user')
    const assistantMsg = chat.messages.find((m) => m.role === 'assistant' && m.content?.trim())
    if (!userMsg || !assistantMsg) return

    titleGeneratedRef.current = true
    const sessionId = chat.session.voiceChatId
    const personaId = selectedPersona.id

    console.log('Auto-generating chat title for session:', sessionId)
    const discussionId = chat.session.discussionId
    generateTitle(userMsg.content, assistantMsg.content).then(async (title) => {
      // Fall back to first user message if AI title generation failed
      const finalTitle = title || userMsg.content.slice(0, 60).trim() || 'Chat'
      console.log('Renaming session to:', finalTitle, title ? '(generated)' : '(fallback)')
      try {
        await renameCloudSession(sessionId, finalTitle)
        // Add session to sidebar immediately (don't wait for session end)
        history.upsertSession({
          id: sessionId,
          discussion_id: discussionId,
          persona: personaId,
          session_type: 'text_chat',
          started_at: new Date().toISOString(),
          ended_at: null,
          summary: finalTitle,
          message_count: chat.messages.length,
        })
      } catch (e) {
        console.warn('Failed to set auto-title:', e)
      }
    })
  }, [chat.messages, chat.session, selectedPersona.id])

  // Reset title tracking when session changes
  useEffect(() => {
    titleGeneratedRef.current = false
  }, [chat.session?.voiceChatId])

  /** Get TTS settings for the selected persona */
  const ttsSettings: TtsSettings | undefined = (() => {
    if (PRIVATE_MODE) {
      return PRIVATE_TTS_VOICE ? { voice: PRIVATE_TTS_VOICE } : undefined
    }
    const cp = cloudPersonas.find((p) => p.id === selectedPersona.id)
    if (!cp) return undefined
    return {
      provider: cp.tts_provider || undefined,
      voice: cp.tts_voice || undefined,
      instructions: cp.tts_speaking_style || undefined,
      speed: cp.speaking_speed || undefined,
      language: voiceLanguage,
    }
  })()

  /** Get AI chat model for the selected persona */
  const chatModel: string | undefined = (() => {
    if (PRIVATE_MODE) return undefined // handled in api.ts via PRIVATE_MODEL
    const cp = cloudPersonas.find((p) => p.id === selectedPersona.id)
    return cp?.chat_model || undefined
  })()

  // Forward the selected model into the chat hook so /voice_chat/start
  // creates the Gemini cache against the same model the user will chat
  // with. Without this, the session starts with the api.ts default rather
  // than the persona's model and the cache never fires.
  useEffect(() => {
    chat.setChatModel(chatModel)
  }, [chat.setChatModel, chatModel])

  // The server refused this persona's model for the user's subscription tier
  // (a lapsed Premium subscription, or a persona synced from a device that
  // could pick it). Re-point the persona to the fallback the server named, so
  // the next message works instead of repeating the same refusal — the bubble
  // from useChat already tells the user what happened. The whole persona is
  // written back because savePersonaSettings replaces every field; sending
  // only chat_model would blank the voice and name.
  useEffect(() => {
    const denial = chat.lastModelAccessDenial
    if (!denial?.fallback_model) return
    const cp = cloudPersonas.find((p) => p.id === selectedPersona.id)
    chat.clearModelAccessDenial()
    if (!cp || cp.chat_model === denial.fallback_model) return
    const updated = { ...cp, chat_model: denial.fallback_model }
    setCloudPersonas((prev) => prev.map((p) => (p.id === cp.id ? updated : p)))
    savePersonaSettings(updated).catch((e) =>
      console.warn('[app] Could not re-point persona after model refusal:', e),
    )
  }, [chat.lastModelAccessDenial, chat.clearModelAccessDenial, cloudPersonas, selectedPersona.id])

  /** Handle persona change: stop active session, clear state, switch persona, go to chat */
  const handleSelectPersona = useCallback(async (persona: Persona) => {
    if (chat.isSessionActive || chat.hasChatView) {
      await chat.stopSession()
    }
    setSelectedPersona(persona)
    history.refresh(persona.id)
    setCurrentView('chat')
  }, [chat, history])

  const handleNewChat = useCallback(async () => {
    if (chat.isSessionActive) {
      await chat.stopSession()
      history.forceRefresh(selectedPersona.id)
    }
    isContinuedSessionRef.current = false
    // Show an empty chat view without creating a backend session — the
    // session is started lazily by useChat.ensureSession() on the first
    // sendMessage. Avoids burning a session row when the user opens the
    // chat view and walks away.
    chat.prepareNewChat(selectedPersona.id)
    setCurrentView('chat')
  }, [chat, history, selectedPersona])

  const handleStopChat = useCallback(async () => {
    await chat.stopSession()
    history.forceRefresh(selectedPersona.id)
  }, [chat, history, selectedPersona])

  /** Wrap sendMessage to pass the persona's AI model and any attachments */
  const handleSendMessage = useCallback((text: string, attachments?: import('./types').ChatAttachment[]) => {
    chat.sendMessage(text, chatModel, attachments)
  }, [chat.sendMessage, chatModel])

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteCloudSession(sessionId)
      history.forceRefresh(selectedPersona.id)
    } catch (e) {
      console.error('Failed to delete session:', e)
    }
  }, [history, selectedPersona])

  const handleRenameSession = useCallback(async (sessionId: string, name: string) => {
    try {
      await renameCloudSession(sessionId, name)
      history.forceRefresh(selectedPersona.id)
    } catch (e) {
      console.error('Failed to rename session:', e)
    }
  }, [history, selectedPersona])

  const handleSelectSession = useCallback(async (session: ChatSession) => {
    try {
      if (chat.isSessionActive) {
        await chat.stopSession()
        history.forceRefresh(selectedPersona.id)
      }
      const loaded = await history.loadSession(session.id)
      chat.loadHistory(session.persona, loaded.messages, session.discussion_id)
      isContinuedSessionRef.current = true
      const persona = personas.find((p) => p.id === session.persona)
      if (persona) setSelectedPersona(persona)
    } catch (e) {
      console.error('Failed to load session:', e)
    }
  }, [chat, history, personas, selectedPersona.id])

  /** Load a chat session by id alone (used when deep-linking from search). */
  const handleOpenSessionById = useCallback(async (sessionId: string) => {
    try {
      if (chat.isSessionActive) {
        await chat.stopSession()
        history.forceRefresh(selectedPersona.id)
      }
      const loaded = await history.loadSession(sessionId)
      chat.loadHistory(loaded.session.persona, loaded.messages, loaded.session.discussion_id)
      isContinuedSessionRef.current = true
      const persona = personas.find((p) => p.id === loaded.session.persona)
      if (persona) setSelectedPersona(persona)
    } catch (e) {
      console.error('Failed to open chat session:', e)
    }
  }, [chat, history, personas, selectedPersona.id])

  // Home screen layout: classic tile landing or the state-first prototype.
  const [homeLayout, setHomeLayoutState] = useState<HomeLayout>(getHomeLayout)
  const switchHomeLayout = useCallback((layout: HomeLayout) => {
    setHomeLayout(layout)
    setHomeLayoutState(layout)
  }, [])

  /** Home persona switch — changes who you talk to without leaving home. */
  const handleHomePersonaChange = useCallback(async (persona: Persona) => {
    if (persona.id === selectedPersona.id) return
    if (chat.isSessionActive || chat.hasChatView) {
      await chat.stopSession()
    }
    setSelectedPersona(persona)
    history.refresh(persona.id)
  }, [chat, history, selectedPersona.id])

  /** Landing page persona click: first click selects, clicking the already-selected persona opens chat. */
  const handleLandingPersonaClick = useCallback((persona: Persona) => {
    if (persona.id === selectedPersona.id) {
      handleSelectPersona(persona)
    } else {
      handleHomePersonaChange(persona)
    }
  }, [selectedPersona.id, handleSelectPersona, handleHomePersonaChange])

  /**
   * Keskustele and an escalated quick line open the same thread with the
   * same persona, carrying the text. A chat already open for this persona is
   * reused so the assistant isn't split across parallel histories.
   */
  const handleOpenChatFromHome = useCallback(async (text?: string) => {
    setFeatureOrigin('landing')
    const sameThread = (chat.isSessionActive || chat.hasChatView)
      && (!chat.session || chat.session.personaId === selectedPersona.id)
    if (!sameThread) {
      if (chat.isSessionActive) await chat.stopSession()
      isContinuedSessionRef.current = false
      chat.prepareNewChat(selectedPersona.id)
    }
    setCurrentView('chat')
    if (text) chat.sendMessage(text, chatModel)
  }, [chat, selectedPersona.id, chatModel])

  /**
   * Continue a quick answer in full chat. The exchange becomes the start of
   * the thread — question and answer are seeded as history and nothing is
   * sent, so the persona picks up where the quick answer left off instead of
   * answering the same question a second time.
   *
   * loadHistory() only sets up the view; the backend session is created on the
   * first real message (useChat.ensureSession) and carries this history with
   * it, so the persona sees what was already said.
   */
  const handleContinueQuickChat = useCallback(async (entry: { question: string; answer: string }) => {
    setFeatureOrigin('landing')
    if (chat.isSessionActive) {
      await chat.stopSession()
      history.forceRefresh(selectedPersona.id)
    }
    // A failed quick answer carries no assistant turn worth replaying.
    const seeded = entry.answer.trim()
      ? [
          { role: 'user', content: entry.question },
          { role: 'assistant', content: entry.answer },
        ]
      : [{ role: 'user', content: entry.question }]
    // Not a continued session: this thread is new, so let the auto-title
    // effect name it from the quick exchange once the next turn completes.
    isContinuedSessionRef.current = false
    chat.loadHistory(selectedPersona.id, seeded, `cloud_${Date.now()}`)
    setCurrentView('chat')
  }, [chat, history, selectedPersona.id])

  // Private mode: auto-start a chat session on mount
  useEffect(() => {
    if (!PRIVATE_MODE || !user) return
    if (chat.isSessionActive || chat.hasChatView) return
    const persona = personas.find((p) => p.id === PRIVATE_PERSONA) || personas[0]
    setSelectedPersona(persona)
    setCurrentView('chat')
    chat.startSession(persona.id)
  }, [user])

  if (authLoading) {
    return <div class="loading">Loading...</div>
  }

  if (!user) {
    return <LoginScreen onSignIn={signIn} isLoading={authLoading} />
  }

  // Wait for account setup (check / auto-create) to complete
  if (!accountReady) {
    return (
      <div class="login-screen">
        <div class="login-card">
          <img
            src="/splash_icon_colorful.png"
            alt="PiPar"
            style={{ width: 96, height: 96, marginBottom: 16, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
          <h1>Meet PiPar</h1>
          <p>Setting up account...</p>
        </div>
      </div>
    )
  }

  // Private mode: show loading until auto-start effect redirects to chat
  if (PRIVATE_MODE && currentView === 'landing') {
    return <div class="loading">Starting private session...</div>
  }

  // Landing page — no sidebar (skip in private mode)
  if (currentView === 'landing' && homeLayout === 'state') {
    return (
      <HomeScreen
        personas={personas}
        selectedPersona={selectedPersona}
        personaImages={personaImages}
        onPersonaChange={handleHomePersonaChange}
        onNavigate={(view, id) => {
          setFeatureOrigin('landing')
          setFocusId(id || null)
          setCurrentView(view as AppView)
        }}
        onOpenChat={handleOpenChatFromHome}
        onContinueQuickChat={handleContinueQuickChat}
        onOpenSession={(sessionId) => {
          setFeatureOrigin('landing')
          handleOpenSessionById(sessionId)
          setCurrentView('chat')
        }}
        onUseClassic={() => switchHomeLayout('classic')}
      />
    )
  }

  if (currentView === 'landing') {
    return (
      <LandingPage
        personas={personas}
        selectedPersonaId={selectedPersona.id}
        onSelectPersona={handleLandingPersonaClick}
        onNavigate={(view, id) => {
          setFeatureOrigin('landing')
          // Chat results need the session pre-loaded into the chat view.
          if (view === 'chat' && id) {
            setFocusId(null)
            handleOpenSessionById(id)
            setCurrentView('chat')
            return
          }
          setFocusId(id || null)
          setCurrentView(view as AppView)
        }}
        userEmail={user.email || 'Unknown'}
        onSignOut={signOut}
        personaImages={personaImages}
        onTryNewHome={STATE_HOME_ENABLED ? () => switchHomeLayout('state') : undefined}
        onContinueQuickChat={handleContinueQuickChat}
        onStartChat={handleOpenChatFromHome}
      />
    )
  }

  // Feature views — with FeatureViewShell (no sidebar)
  const featureTitle = VIEW_TITLES[currentView]
  const hasActiveChat = chat.isSessionActive || chat.hasChatView
  const backToChat = featureOrigin === 'chat' || hasActiveChat
  // Force remount of views when cloud tools have modified data
  const refreshKey = chat.toolResults.length
  if (featureTitle) {
    const goBack = () => setCurrentView(backToChat ? 'chat' : 'landing')
    return (
      <FeatureViewShell title={featureTitle} onGoHome={goBack} backToChat={backToChat}>
        {currentView === 'tasks' && <TasksView key={`tasks-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'notes' && <NotesView key={`notes-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'projects' && <ProjectsView key={`projects-${refreshKey}`} />}
        {currentView === 'email' && <EmailView key={`email-${refreshKey}`} />}
        {currentView === 'calendar' && <CalendarView key={`calendar-${refreshKey}`} />}
        {currentView === 'calls' && <CallsView key={`calls-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'alerts' && <AlertsView key={`alerts-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'box' && <BoxView key={`box-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'files' && <FilesView key={`files-${refreshKey}`} focusId={focusId || undefined} />}
        {currentView === 'settings' && <SettingsView personas={personas} selectedPersona={selectedPersona} themeCtx={themeCtx} onPersonaSettingsSaved={refreshCloudPersonas} userEmail={user.email || ''} onSignOut={signOut} />}
        {currentView === 'user' && <UserView />}
        {currentView === 'apps' && <AppsView key={`apps-${refreshKey}`} />}
      </FeatureViewShell>
    )
  }

  // Chat, Data, or Test view — with sidebar
  return (
    <div class="app">
      {sidebarVisible && (
        <Sidebar
          userEmail={user.email || 'Unknown'}
          onSignOut={signOut}
          personas={personas}
          selectedPersona={selectedPersona}
          onSelectPersona={handleSelectPersona}
          onNewChat={handleNewChat}
          onStopChat={handleStopChat}
          // Only show "Stop Chat" when a real backend session is live.
          // hasChatView is also true for pending state (loaded history or
          // a fresh New Chat that hasn't sent its first message yet) — in
          // those cases there's nothing to stop, so keep showing "New Chat".
          isSessionActive={chat.isSessionActive}
          sessions={history.sessions}
          sessionsLoading={history.isLoading}
          sessionsLoadingMore={history.isLoadingMore}
          hasMoreSessions={history.hasMore}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onRenameSession={handleRenameSession}
          onRefreshSessions={history.refresh}
          onLoadMoreSessions={history.loadMore}
          onGoHome={() => setCurrentView('landing')}
          personaImages={personaImages}
          onClose={() => setSidebarVisible(false)}
          onNavigate={(view) => { setFeatureOrigin('chat'); setCurrentView(view as AppView) }}
        />
      )}
      {callOpen && (
        <VoiceCallScreen
          options={{
            personaId: selectedPersona.id,
            personaName: selectedPersona.name,
            model: chatModel,
            ttsProvider: ttsSettings?.provider,
            voice: ttsSettings?.voice,
            language: voiceLanguage,
            speed: ttsSettings?.speed,
            instructions: ttsSettings?.instructions,
          }}
          personaEmoji={selectedPersona.emoji}
          personaImageUrl={personaImages[selectedPersona.id]}
          onClose={() => {
            setCallOpen(false)
            // The call is saved as a conversation: show it in the list.
            history.refresh(selectedPersona.id)
          }}
        />
      )}
      <main class="app__main">
        <ChatView
          messages={chat.messages}
          isLoading={chat.isLoading}
          isSessionActive={chat.isSessionActive || chat.hasChatView}
          voiceChatId={chat.session?.voiceChatId ?? null}
          onSend={handleSendMessage}
          onStartSession={handleNewChat}
          onEditMessage={chat.editMessageAndResend}
          onDeleteMessage={chat.deleteMessageAndNewer}
          personaName={selectedPersona.name}
          personaImageUrl={personaImages[selectedPersona.id]}
          onStartCall={() => setCallOpen(true)}
          ttsSettings={ttsSettings}
          onNavigate={(view) => { setFeatureOrigin('chat'); setCurrentView(view as AppView) }}
          cloudPersona={cloudPersonas.find((cp) => cp.id === selectedPersona.id)}
          sidebarVisible={sidebarVisible}
          onToggleSidebar={() => setSidebarVisible((v) => !v)}
          personas={personas}
          selectedPersona={selectedPersona}
          onSelectPersona={handleSelectPersona}
          userEmail={user.email || ''}
          onPersonaUpdated={refreshCloudPersonas}
        />
        {chat.error && (
          <div class="app__error">{chat.error}</div>
        )}
      </main>
      {chat.generatedContent && (
        <GeneratedContentPreview
          content={chat.generatedContent}
          onClose={chat.clearGeneratedContent}
          userRequest={precedingUserRequest(chat.messages)}
        />
      )}
      {chat.pendingEmailReview && (
        <EmailReviewDialog
          review={chat.pendingEmailReview}
          onClose={chat.clearPendingEmailReview}
        />
      )}
    </div>
  )
}
